import React, { useState, useEffect } from "react";
import { Button, Form, Alert, Spinner } from "react-bootstrap";
import axiosInstance from "../../api/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import colors from "../../constants/colors";

const EditableWrapper = ({ section, children, style }) => {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [newContent, setNewContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isHovering, setIsHovering] = useState(false);
  const isAdmin = user && user.isAdmin;

  useEffect(() => {
    const fetchContent = async () => {
      setInitialLoading(true);
      try {
        const response = await axiosInstance.get(`/content/${section}`);
        setContent(response.value || "");
        
      } catch (err) {
        console.error("Erro ao carregar o conteúdo:", err);
        
        if (isAdmin) {
            setError("Erro ao carregar o conteúdo.");
            const res = await axiosInstance.put(`/content/${section}`, { value: "" });
            setContent(res.value || "");
        }
      } finally {
        setInitialLoading(false);
      }
    };
    fetchContent();
  }, [section, isAdmin]);

  const handleEditClick = () => {
    setNewContent(content);
    setIsEditing(true);
    setError("");
    setSuccessMsg("");
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError("");
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axiosInstance.put(`/content/${section}`, { value: newContent });
      setContent(newContent);
      setIsEditing(false);
      setSuccessMsg("Conteúdo atualizado com sucesso.");
      
      // Auto-dismiss success message after 3 seconds
      setTimeout(() => {
        setSuccessMsg("");
      }, 3000);
    } catch (err) {
      setError("Erro ao atualizar o conteúdo.");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" role="status" />
        <span className="visually-hidden">A carregar...</span>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', ...style }}>
      {loading && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255,255,255,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <Spinner animation="border" role="status" />
        </div>
      )}
      
      {error && isAdmin && (
        <Alert 
          variant="danger" 
          style={{
            position: 'absolute',
            top: '-45px',
            left: 0,
            right: 0,
            zIndex: 1000
          }}
        >
          {error.toString()}
        </Alert>
      )}
      
      {successMsg && isAdmin && (
        <Alert 
          variant="success" 
          style={{
            position: 'absolute',
            top: '-45px',
            left: 0,
            right: 0,
            zIndex: 1000
          }}
        >
          {successMsg.toString()}
        </Alert>
      )}

      {isEditing ? (
        <div style={{ backgroundColor: 'rgba(255,255,255,0.95)', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              rows={5}
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              style={{
                border: `1px solid ${colors.secondary}`,
                borderRadius: '8px',
                padding: '12px'
              }}
            />
          </Form.Group>
          <div className="d-flex gap-2">
            <Button
              onClick={handleSave}
              disabled={loading}
              style={{
                backgroundColor: colors.secondary,
                color: colors.light,
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                transition: 'all 0.2s ease'
              }}
            >
              Guardar
            </Button>
            <Button
              variant="outline-secondary"
              onClick={handleCancel}
              disabled={loading}
              style={{
                borderRadius: '6px',
                padding: '8px 16px'
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <div 
          className={isAdmin ? "content-editable" : ""}
          onClick={isAdmin ? handleEditClick : undefined}
          onMouseEnter={isAdmin ? () => setIsHovering(true) : undefined}
          onMouseLeave={isAdmin ? () => setIsHovering(false) : undefined}
          style={{
            cursor: isAdmin ? 'pointer' : 'default',
            position: 'relative',
            transition: 'all 0.2s ease',
            padding: isAdmin ? '8px' : '0',
            borderRadius: '8px',
            backgroundColor: isHovering ? 'rgba(255,255,255,0.05)' : 'transparent',
            ...style
          }}
        >
          {content ? (
            <div dangerouslySetInnerHTML={{ __html: content }} />
          ) : (
            children
          )}
          
          {isAdmin && isHovering && (
            <div 
              style={{
                position: 'absolute',
                top: '50%',
                right: '10px',
                transform: 'translateY(-50%)',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                backgroundColor: colors.secondary,
                color: colors.light,
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                opacity: 0.9
              }}
            >
              <i className="fas fa-pencil-alt"></i> Editar
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EditableWrapper;
